import React, { useEffect, useState } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import PaginationComponent from '../commonComponents/PaginationComponent';
import { FaCheckCircle, FaExclamationCircle, FaRobot, FaFileAlt, FaTag, FaEye } from 'react-icons/fa';
import { BsToggleOn, BsToggleOff } from 'react-icons/bs';
import { url } from '../../util/config';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DataConverseDrawer from './DataConverseDrawer';
import DataConverseChatbot from './DataConverseChatbot';



const PAGE_SIZE = 9;

const DataConverseComponent = () => {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [titleFilter, setTitleFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [metadataFilter, setMetadataFilter] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerData, setDrawerData] = useState(null);
    const [chatbotOpen, setChatbotOpen] = useState(false);
    const [activeChatbotId, setActiveChatbotId] = useState(null);
    const [chatHistories, setChatHistories] = useState({}); // { [id]: [messages] }

    

    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${url}/dataconverse`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const result = await response.json();
                setData(result);
                setFilteredData(result);
            } catch {
                setData([]);
                setFilteredData([]);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        let temp = [...data];
        if (titleFilter) {
            temp = temp.filter(item => item.title?.toLowerCase().includes(titleFilter.toLowerCase()));
        }
        if (statusFilter) {
            temp = temp.filter(item => String(item.status) === statusFilter);
        }
        if (metadataFilter) {
            temp = temp.filter(item => item.metadata?.toLowerCase().includes(metadataFilter.toLowerCase()));
        }
        setFilteredData(temp);
        setCurrentPage(1);
    }, [titleFilter, statusFilter, metadataFilter, data]);

    const handleOpenChatbot = (id) => {
        setActiveChatbotId(id);
        setChatbotOpen(true);
        // Initialize history if not present
        setChatHistories(prev => ({
            ...prev,
            [id]: prev[id] || [
                { from: 'bot', text: 'Welcome to Data Converse AI Chatbot, How I can help you?' }
            ]
        }));
    };

    const handleChatMessage = (id, newMessages) => {
        setChatHistories(prev => ({
            ...prev,
            [id]: newMessages
        }));
    };

    const updateFileStatusLocally = (fileId, newStatus) => {
        setData(prev =>
            prev.map(item =>
                item._id === drawerData._id
                    ? {
                        ...item,
                        files: item.files.map(f =>
                            f._id === fileId ? { ...f, status: newStatus } : f
                        )
                    }
                    : item
            )
        );
        setFilteredData(prev =>
            prev.map(item =>
                item._id === drawerData._id
                    ? {
                        ...item,
                        files: item.files.map(f =>
                            f._id === fileId ? { ...f, status: newStatus } : f
                        )
                    }
                    : item
            )
        );
        setDrawerData(prev =>
            prev
                ? {
                    ...prev,
                    files: prev.files.map(f =>
                        f._id === fileId ? { ...f, status: newStatus } : f
                    )
                }
                : prev
        );
    };

    // Update files locally after upload
    const updateFilesLocally = (dataconverseId, newFiles) => {
        setData(prev =>
            prev.map(item =>
                item._id === dataconverseId
                    ? { ...item, files: [...item.files, ...newFiles] }
                    : item
            )
        );
        setFilteredData(prev =>
            prev.map(item =>
                item._id === dataconverseId
                    ? { ...item, files: [...item.files, ...newFiles] }
                    : item
            )
        );
        setDrawerData(prev =>
            prev
                ? { ...prev, files: [...prev.files, ...newFiles] }
                : prev
        );
    };

    const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
    const paginatedData = filteredData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const handleStatusToggle = (id, currentStatus) => {
        fetch(`${url}/dataconverse/${id}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify({ status: !currentStatus }),
        }).then(() => {
            setData(prev =>
                prev.map(item =>
                    item._id === id ? { ...item, status: !currentStatus } : item
                )
            );
            setFilteredData(prev =>
                prev.map(item =>
                    item._id === id ? { ...item, status: !currentStatus } : item
                )
            );
            toast.success(currentStatus ? "De-activated successfully" : "Activated successfully", { autoClose: 2000 });
        });
    };

    // Card CSS
    const cardStyle = {
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        borderRadius: '16px',
        background: '#fff',
        marginBottom: '24px',
        minHeight: '220px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'box-shadow 0.2s',
        border: '1px solid #e3e3e3'
    };

    const cardHeaderStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 16px 0 16px'
    };

    const cardBodyStyle = {
        display: 'flex',
        flexDirection: 'row',
        padding: '0 16px',
        flex: 1
    };

    const descStyle = {
        flex: '0 0 80%',
        fontSize: '15px',
        color: '#333',
        paddingRight: '12px',
        wordBreak: 'break-word'
    };

    const statusStyle = {
        flex: '0 0 20%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'center'
    };

    const cardFooterStyle = {
        display: 'flex',
        alignItems: 'center',
        borderTop: '1px solid #f0f0f0',
        padding: '12px 16px',
        background: '#f8f9fa',
        borderRadius: '0 0 16px 16px'
    };

    return (
        <WrapperComponent>
            <div className="container mt-4">
                <ToastContainer />
                <h2 className="mb-4">Data Converse AI</h2>
                {/* Filters and Create Button */}
                <div className="row mb-3">
                    <div className="col-md-3">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Filter by Title"
                            value={titleFilter}
                            onChange={e => setTitleFilter(e.target.value)}
                        />
                    </div>
                    <div className="col-md-3">
                        <select
                            className="form-select"
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value)}
                        >
                            <option value="">All Status</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Filter by Metadata"
                            value={metadataFilter}
                            onChange={e => setMetadataFilter(e.target.value)}
                        />
                    </div>
                    <div className="col-md-3 d-flex align-items-end justify-content-end">
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/createdataconverse')}
                        >
                            Create DataConverse
                        </button>
                    </div>
                </div>
                {/* Cards */}
                <div className="row">
                    {paginatedData.length === 0 && (
                        <div className="col-12 text-center text-muted py-5">No Data Converse found.</div>
                    )}
                    {paginatedData.map(item => (
                        <div className="col-md-4" key={item._id}>
                            <div style={cardStyle} className="data-converse-card">
                                {/* Card Header */}
                                <div style={cardHeaderStyle}>
                                    <div
                                        style={{
                                            fontWeight: 600,
                                            fontSize: 16,
                                            color: '#007bff',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '100%' // or a fixed width like '250px' if needed
                                        }}
                                        title={item.title} // shows full title on hover
                                        >
                                        {item.title}
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button
                                            className="btn btn-sm"
                                            title="View"
                                            style={{ background: '#f0f0f0' }}
                                            onClick={() => {
                                                setDrawerData(item);
                                                setDrawerOpen(true);
                                            }}
                                        >
                                            <FaEye style={{ color: '#007bff', fontSize: '22px' }} />
                                        </button>
                                        <button
                                            className="btn btn-sm"
                                            title={item.status ? 'Deactivate' : 'Activate'}
                                            onClick={() => handleStatusToggle(item._id, item.status)}
                                            style={{ background: '#f0f0f0' }}
                                        >
                                            {item.status
                                                ? <BsToggleOff style={{ fontSize: "22px", color: "red" }} />
                                                : <BsToggleOn style={{ fontSize: "22px", color: "green" }} />}
                                        </button>
                                        {/* <button className="btn btn-sm" title="Files" style={{ background: '#f0f0f0' }}>
                                            <FaFileAlt style={{ color: 'green', fontSize: '22px' }} />
                                        </button> */}
                                    </div>
                                </div>
                                {/* Card Body */}
                                <div style={cardBodyStyle}>
                                    <div style={descStyle}>
                                        <div style={{ marginBottom: 8 }}>
                                            <span style={{ fontWeight: 500 }}>Description: </span>
                                            {item.description && item.description.length > 100
                                                ? item.description.slice(0, 100) + '...'
                                                : item.description}
                                        </div>
                                        <div style={{ fontSize: 14, color: '#555' }}>
                                            <span style={{ fontWeight: 500 }}>Uploaded Files: </span>
                                            <span style={{ textDecoration: 'underline', color: '#007bff', cursor: 'pointer' }}>
                                                {Array.isArray(item.files) ? item.files.length : 0}
                                            </span>
                                        </div>
                                    </div>
                                    {/* Chatbot image at right bottom corner */}
                                    <div
                                        style={{
                                            position: 'absolute',
                                            right: 25,
                                            bottom: 75,
                                            zIndex: 2,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '48px',
                                            height: '48px',
                                        }}
                                        onClick={() => handleOpenChatbot(item._id)}
                                        title="Open Chatbot"
                                    >
                                        <img
                                            src="/chatbot.png"
                                            alt="Chatbot"
                                            style={{
                                                width: '48px',
                                                height: '48px',
                                                objectFit: 'contain',
                                                display: 'block',
                                            }}
                                        />
                                    </div>
                                </div>
                                {/* Card Footer */}
                                <div style={cardFooterStyle}>
                                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                        <FaTag style={{ color: '#6c757d', marginRight: 8 }} />
                                        <span style={{ fontWeight: 500, color: '#333' }}>{item.metatag}</span>
                                    </div>
                                    <div>
                                        <span className={`badge ${item.status ? 'bg-success' : 'bg-danger'}`}>
                                            {item.status ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {/* Pagination */}
                <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />                
                <DataConverseDrawer
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    data={drawerData}
                    updateFilesLocally={updateFilesLocally}
                    updateFileStatusLocally={updateFileStatusLocally}
                />
                <DataConverseChatbot
                    open={chatbotOpen}
                    onClose={() => setChatbotOpen(false)}
                    dataConverseId={activeChatbotId}
                    messages={chatHistories[activeChatbotId] || []}
                    onMessagesChange={msgs => handleChatMessage(activeChatbotId, msgs)}
                />
            </div>
            <style>{`
                .data-converse-card:hover {
                    box-shadow: 0 4px 16px rgba(0,0,0,0.13);
                    border-color: #007bff;
                }
            `}</style>
        </WrapperComponent>
    );
};

export default DataConverseComponent;